import React, { useState } from 'react';
import { useAnalytics } from './AdvancedAnalyticsProvider';
import { Download, FileText, FileSpreadsheet, Image } from 'lucide-react';
// Import ExcelExportService dynamically to avoid SSR issues and import conflicts
import { pdfExportService } from '../../services/PDFExportService.backend';

interface WorkingExportProps {
  dashboardRef: React.RefObject<HTMLDivElement>;
}

export const WorkingExport: React.FC<WorkingExportProps> = ({ dashboardRef }) => {
  const { chartData, overallStats, filters } = useAnalytics();
  const [isExporting, setIsExporting] = useState(false);
  const [exportFormat, setExportFormat] = useState<'pdf' | 'excel' | 'png'>('excel');

  // Add event listener for dashboard export events
  React.useEffect(() => {
    const handleDashboardExport = (event: CustomEvent) => {
      console.log('WorkingExport: Received dashboardExport event:', event.detail);
      
      if (event.detail && event.detail.format) {
        console.log(`WorkingExport: Handling export for format: ${event.detail.format}`);
        handleExportForFormat(event.detail.format);
      }
    };

    window.addEventListener('dashboardExport', handleDashboardExport as EventListener);
    
    return () => {
      window.removeEventListener('dashboardExport', handleDashboardExport as EventListener);
    };
  }, [chartData, overallStats, filters]);

  const handleExportForFormat = async (format: 'pdf' | 'excel' | 'png') => {
    console.log(`WorkingExport: Direct export called for format: ${format}`);
    setExportFormat(format);
    await handleExport();
  };

  const downloadFile = (content: string, filename: string, mimeType: string) => {
    const blob = new Blob([content], { type: mimeType });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    
    link.href = url;
    link.download = filename;
    link.style.visibility = 'hidden';
    
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const exportAsExcel = async () => {
    setIsExporting(true);
    try {
      console.log('WorkingExport: Starting Excel export...');
      
      // Import ExcelExportService dynamically to avoid SSR issues
      const { excelExportService } = await import('../../services/ExcelExportService');
      
      // Prepare professional Excel data
      const workbookData = [
        {
          'Report Type': 'Analytics Dashboard Export',
          'Generated Date': new Date().toLocaleString(),
          'Date Range': `${filters.dateRange?.start ? new Date(filters.dateRange.start).toLocaleDateString() : 'N/A'} - ${filters.dateRange?.end ? new Date(filters.dateRange.end).toLocaleDateString() : 'N/A'}`,
          'Town': filters.townId === 'all' ? 'All Towns' : filters.townId,
          'Total Revenue': overallStats.totalRevenue || 0,
          'Total Members': overallStats.totalMembers || 0,
          'Average Revenue per Member': overallStats.averageRevenuePerMember || 0,
          'Member Growth Rate (%)': overallStats.memberGrowthRate || 0,
          'Revenue Growth Rate (%)': overallStats.revenueGrowthRate || 0
        },
        {
          'Activity Type': 'Contributions',
          'Count': overallStats.activityBreakdown?.contributions || 0,
          'Details': 'Member contributions to the system'
        },
        {
          'Activity Type': 'Benefits',
          'Count': overallStats.activityBreakdown?.benefits || 0,
          'Details': 'Member benefits and assistance'
        },
        {
          'Activity Type': 'Events',
          'Count': overallStats.activityBreakdown?.events || 0,
          'Details': 'Community events organized'
        },
        {
          'Activity Type': 'Expenses',
          'Count': overallStats.activityBreakdown?.expenses || 0,
          'Details': 'System operational expenses'
        }
      ];

      // Prepare detailed data
      const detailedData = chartData.map(point => ({
        'Time Period': point.time,
        'Revenue': point.revenue || 0,
        'Members': point.members || 0,
        'Activities': point.activities || 0,
        'Contributions': point.contributions || 0,
        'Benefits': point.benefits || 0,
        'Events': point.events || 0,
        'Expenses': point.expenses || 0
      }));

      // Use professional Excel service
      const filename = `Dashboard_Analytics_${new Date().toISOString().slice(0, 10)}`;
      const excelBlob = await excelExportService.generateSimpleExcel(
        [...workbookData, {'': ''}, ...detailedData],
        filename,
        'Dashboard Analytics'
      );
      
      // Trigger download
      const url = URL.createObjectURL(excelBlob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${filename}.xlsx`;
      link.style.visibility = 'hidden';
      
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      
      console.log('WorkingExport: Excel export completed');
    } catch (error) {
      console.error('WorkingExport: Excel export failed:', error);
      alert('Failed to export Excel. Please try again.');
    } finally {
      setIsExporting(false);
    }
  };

  const exportAsPDF = async () => {
    setIsExporting(true);
    try {
      console.log('WorkingExport: Starting PDF export...');
      
      // Prepare chart data for PDF
      const chartDataForPDF = chartData.map(point => ({
        time: point.time,
        revenue: point.revenue || 0,
        members: point.members || 0,
        activities: point.activities || 0,
        contributions: point.contributions || 0,
        benefits: point.benefits || 0,
        events: point.events || 0,
        expenses: point.expenses || 0
      }));
      
      // Generate PDF using the professional service
      const pdfBlob = await pdfExportService.generateReportWithChartsData(
        chartDataForPDF,
        chartDataForPDF,
        'financial',
        {
          header: {
            title: 'Dashboard Analytics Report',
            dateRange: `${filters.dateRange?.start ? new Date(filters.dateRange.start).toLocaleDateString() : 'N/A'} - ${filters.dateRange?.end ? new Date(filters.dateRange.end).toLocaleDateString() : 'N/A'}`,
            townName: filters.townId === 'all' ? 'All Towns' : filters.townId,
            generatedDate: new Date().toLocaleDateString()
          }
        }
      );
      
      // Trigger download
      const url = URL.createObjectURL(pdfBlob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `Dashboard_Analytics_Report_${new Date().toISOString().slice(0, 10)}.pdf`;
      link.style.visibility = 'hidden';
      
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      
      console.log('WorkingExport: PDF export completed');
    } catch (error) {
      console.error('WorkingExport: PDF export failed:', error);
      alert('Failed to export PDF. Please try again.');
    } finally {
      setIsExporting(false);
    }
  };

  const exportAsPNG = async () => {
    setIsExporting(true);
    try {
      console.log('WorkingExport: Starting PNG export...');
      
      // Create a comprehensive dashboard screenshot
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      if (!ctx) throw new Error('Canvas context not available');

      // High-quality canvas
      const scale = 2;
      canvas.width = 1000 * scale;
      canvas.height = 700 * scale;
      ctx.scale(scale, scale);
      
      // Background
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      
      // Header
      ctx.fillStyle = '#1f2937';
      ctx.font = 'bold 28px Arial';
      ctx.fillText('Ghana Town Membership System', 20, 40);
      ctx.font = 'bold 20px Arial';
      ctx.fillText('Dashboard Analytics Report', 20, 70);
      
      // Date and filters
      ctx.font = '14px Arial';
      ctx.fillStyle = '#6b7280';
      ctx.fillText(`Generated: ${new Date().toLocaleString()}`, 20, 100);
      ctx.fillText(`Town: ${filters.townId === 'all' ? 'All Towns' : filters.townId}`, 20, 120);
      
      // Summary statistics
      ctx.font = 'bold 18px Arial';
      ctx.fillStyle = '#1f2937';
      ctx.fillText('Key Metrics', 20, 160);
      
      ctx.font = '14px Arial';
      ctx.fillStyle = '#374151';
      const metrics = [
        `Total Revenue: GH₵${overallStats.totalRevenue?.toLocaleString() || '0'}`,
        `Total Members: ${(overallStats.totalMembers || 0).toLocaleString()}`,
        `Avg Revenue/Member: GH₵${overallStats.averageRevenuePerMember?.toLocaleString() || '0'}`,
        `Member Growth: ${overallStats.memberGrowthRate?.toFixed(2) || '0'}%`,
        `Revenue Growth: ${overallStats.revenueGrowthRate?.toFixed(2) || '0'}%`,
        `Total Activities: ${(overallStats.activityBreakdown?.contributions || 0) + (overallStats.activityBreakdown?.benefits || 0) + (overallStats.activityBreakdown?.events || 0) + (overallStats.activityBreakdown?.expenses || 0)}`
      ];
      
      metrics.forEach((metric, index) => {
        ctx.fillText(metric, 20, 190 + (index * 25));
      });
      
      // Activity breakdown
      ctx.font = 'bold 18px Arial';
      ctx.fillStyle = '#1f2937';
      ctx.fillText('Activity Breakdown', 500, 160);
      
      ctx.font = '14px Arial';
      ctx.fillStyle = '#374151';
      const activities = [
        `Contributions: ${overallStats.activityBreakdown?.contributions || 0}`,
        `Benefits: ${overallStats.activityBreakdown?.benefits || 0}`,
        `Events: ${overallStats.activityBreakdown?.events || 0}`,
        `Expenses: ${overallStats.activityBreakdown?.expenses || 0}`
      ];
      
      activities.forEach((activity, index) => {
        ctx.fillText(activity, 500, 190 + (index * 25));
      });
      
      // Revenue trend chart
      if (chartData.length > 0) {
        ctx.font = 'bold 16px Arial';
        ctx.fillStyle = '#1f2937';
        ctx.fillText('Revenue Trend (Last 10 periods)', 20, 380);
        
        // Chart area
        const chartX = 20;
        const chartY = 400;
        const chartWidth = 460;
        const chartHeight = 200;
        
        // Chart background
        ctx.strokeStyle = '#e5e7eb';
        ctx.strokeRect(chartX, chartY, chartWidth, chartHeight);
        
        // Grid lines
        ctx.strokeStyle = '#f3f4f6';
        for (let i = 1; i < 5; i++) {
          const y = chartY + (i * chartHeight / 5);
          ctx.beginPath();
          ctx.moveTo(chartX, y);
          ctx.lineTo(chartX + chartWidth, y);
          ctx.stroke();
        }
        
        // Bars
        const maxRevenue = Math.max(...chartData.slice(0, 10).map(d => d.revenue || 0));
        const barWidth = chartWidth / Math.min(chartData.length, 10) - 4;
        
        chartData.slice(0, 10).forEach((point, index) => {
          const barHeight = maxRevenue > 0 ? (point.revenue || 0) / maxRevenue * (chartHeight - 20) : 0;
          const x = chartX + 2 + (index * (barWidth + 4));
          const y = chartY + chartHeight - barHeight - 10;
          
          // Gradient bar
          const gradient = ctx.createLinearGradient(x, y, x, y + barHeight);
          gradient.addColorStop(0, '#3b82f6');
          gradient.addColorStop(1, '#1d4ed8');
          ctx.fillStyle = gradient;
          ctx.fillRect(x, y, barWidth, barHeight);
        });
        
        // Y-axis labels
        ctx.font = '12px Arial';
        ctx.fillStyle = '#6b7280';
        ctx.textAlign = 'right';
        for (let i = 0; i <= 5; i++) {
          const value = (maxRevenue * i / 5).toLocaleString();
          const y = chartY + chartHeight - (i * chartHeight / 5);
          ctx.fillText(`GH₵${value}`, chartX - 5, y + 4);
        }
        ctx.textAlign = 'left';
      }
      
      // Footer
      ctx.font = '12px Arial';
      ctx.fillStyle = '#9ca3af';
      ctx.fillText('Generated by Ghana Town Membership System', 20, 650);
      
      // Download
      const link = document.createElement('a');
      link.download = `Dashboard_Analytics_${new Date().toISOString().slice(0, 10)}.png`;
      link.href = canvas.toDataURL('image/png');
      link.click();
      
      console.log('WorkingExport: PNG export completed');
    } catch (error) {
      console.error('WorkingExport: PNG export failed:', error);
      alert('Failed to export PNG. Please try again.');
    } finally {
      setIsExporting(false);
    }
  };

  const handleExport = async () => {
    switch (exportFormat) {
      case 'excel':
        await exportAsExcel();
        break;
      case 'pdf':
        await exportAsPDF();
        break;
      case 'png':
        await exportAsPNG();
        break;
    }
  };

  return (
    <div className="bg-white dark:bg-dark-bg-secondary rounded-lg shadow p-6">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-dark-text-primary">Export Dashboard</h3>
        <div className="text-sm text-gray-500 dark:text-gray-400">
          Export current dashboard data
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Export Format Selection */}
        <div className="space-y-4">
          <h4 className="text-md font-semibold text-gray-900 dark:text-dark-text-primary">Export Format</h4>
          
          <div className="grid grid-cols-1 gap-3">
            <button
              onClick={() => setExportFormat('excel')}
              className={`p-4 border rounded-lg text-center transition-colors ${
                exportFormat === 'excel'
                  ? 'border-green-500 bg-green-50 dark:bg-green-900/20'
                  : 'border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-800'
              }`}
            >
              <FileSpreadsheet className="h-8 w-8 mx-auto mb-2 text-green-600 dark:text-green-400" />
              <div className="font-medium">Excel/CSV Data</div>
              <div className="text-xs text-gray-500 dark:text-gray-400">Raw data for analysis</div>
            </button>

            <button
              onClick={() => setExportFormat('pdf')}
              className={`p-4 border rounded-lg text-center transition-colors ${
                exportFormat === 'pdf'
                  ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20'
                  : 'border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-800'
              }`}
            >
              <FileText className="h-8 w-8 mx-auto mb-2 text-blue-600 dark:text-blue-400" />
              <div className="font-medium">PDF Report</div>
              <div className="text-xs text-gray-500 dark:text-gray-400">Professional report format</div>
            </button>

            <button
              onClick={() => setExportFormat('png')}
              className={`p-4 border rounded-lg text-center transition-colors ${
                exportFormat === 'png'
                  ? 'border-purple-500 bg-purple-50 dark:bg-purple-900/20'
                  : 'border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-800'
              }`}
            >
              <Image className="h-8 w-8 mx-auto mb-2 text-purple-600 dark:text-purple-400" />
              <div className="font-medium">PNG Image</div>
              <div className="text-xs text-gray-500 dark:text-gray-400">Visual dashboard snapshot</div>
            </button>
          </div>
        </div>

        {/* Export Actions */}
        <div className="space-y-4">
          <h4 className="text-md font-semibold text-gray-900 dark:text-dark-text-primary">Export Actions</h4>
          
          <div className="space-y-3">
            <button
              onClick={handleExport}
              disabled={isExporting}
              className="w-full flex items-center gap-3 px-4 py-3 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed text-white rounded-lg transition-colors"
            >
              <Download className={`h-5 w-5 ${isExporting ? 'animate-spin' : ''}`} />
              <span className="text-lg font-semibold">
                {isExporting ? 'Exporting...' : `Export as ${exportFormat.toUpperCase()}`}
              </span>
            </button>
          </div>

          <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
            <div className="flex items-start gap-3">
              <div className="w-2 h-2 bg-blue-500 rounded-full mt-2 flex-shrink-0"></div>
              <div>
                <h5 className="font-medium text-blue-800 dark:text-blue-300">Export Ready</h5>
                <ul className="text-sm text-blue-700 dark:text-blue-200 mt-1 space-y-1">
                  <li>• Excel format includes all data with professional formatting</li>
                  <li>• PDF format provides comprehensive report with charts</li>
                  <li>• PNG format creates visual dashboard image with metrics</li>
                  <li>• All exports work offline and include current filters</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default WorkingExport;